import { ClientStorage, MedplumClient, MemoryStorage } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { config } from '../../../config';
import { isInteractiveUserProfile } from '../../../lib/isInteractiveMedplumProfile';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

function isClientApplicationProfile(profile: unknown): boolean {
  return (profile as { resourceType?: string } | undefined)?.resourceType === 'ClientApplication';
}

const INTAKE_OAUTH_NOT_CONFIGURED =
  'Public intake OAuth is not configured. Set VITE_MEDPLUM_CLIENT_ID and VITE_MEDPLUM_CLIENT_SECRET.';

/**
 * Which ClientApplication signs in. Guest join passes its own (P7-A design Decision 38); intake defaults to the SPA's.
 * Pass a module-level constant: the hooks re-run their login when the object changes.
 */
export type ClientCredentials = { clientId: string; clientSecret: string; notConfigured: string };

function intakeCredentials(): ClientCredentials {
  const { clientId, clientSecret } = config.medplum;
  return { clientId, clientSecret, notConfigured: INTAKE_OAUTH_NOT_CONFIGURED };
}

export type IntakeAuthAction = 'ready' | 'login' | 'staff-required';

/**
 * What the app-wide client must do for the current profile.
 * - Staff add-client needs a Practitioner session.
 * - A signed-in human (Patient, Practitioner, RelatedPerson) is reused, never replaced.
 * - A restored ClientApplication login is reused only for the default (SPA) credentials. With explicit credentials
 *   (guest join) it holds no secret, and client_credentials issues no refresh token, so its token could not be
 *   renewed: log in again instead.
 */
export function intakeAuthAction(
  profile: unknown,
  opts: { staffAddClient: boolean; hasCredentials: boolean },
): IntakeAuthAction {
  const type = (profile as { resourceType?: string } | undefined)?.resourceType;
  if (opts.staffAddClient) return type === 'Practitioner' ? 'ready' : 'staff-required';
  if (type === 'Patient' || isInteractiveUserProfile(profile as never)) return 'ready';
  if (type === 'ClientApplication' && !opts.hasCredentials) return 'ready';
  return 'login';
}

/**
 * Ensures intake (when there is no interactive Practitioner/Patient session) uses the
 * dedicated OAuth client_credentials ClientApplication.
 *
 * We intentionally do **not** reuse Practitioner/Patient sessions from the main app:
 * those AccessPolicies cannot search all open referral-request Tasks.
 */
export function useIntakeClientAuth(opts?: {
  /** Staff `/settings/users/add-client` — keep Practitioner session; no public OAuth. */
  staffAddClient?: boolean;
  /**
   * Default true. False holds off entirely (never `startClientLogin`) — e.g. while Medplum is
   * still restoring a stored login, whose not-yet-loaded profile would look anonymous.
   */
  enabled?: boolean;
  /**
   * Default: the SPA client (`VITE_MEDPLUM_CLIENT_ID`), and a restored ClientApplication session counts as ready.
   * When given, the hook always logs in with them (never reuses a restored session), so the client holds the secret
   * and renews its token.
   */
  credentials?: ClientCredentials;
}): { ready: boolean; authError: string | null } {
  const medplum = useMedplum();
  const staffAddClient = Boolean(opts?.staffAddClient);
  const enabled = opts?.enabled ?? true;
  const credentials = opts?.credentials;
  const [ready, setReady] = useState(() => {
    // With explicit credentials (guest join) the hook always logs in, so the secret is held for token renewal.
    if (credentials) return false;
    const profile = medplum.getProfile();
    if (profile?.resourceType === 'Patient') return true;
    return isClientApplicationProfile(profile);
  });
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      setAuthError(null);
      return undefined;
    }
    const action = intakeAuthAction(medplum.getProfile(), { staffAddClient, hasCredentials: Boolean(credentials) });
    if (action === 'staff-required') {
      setReady(false);
      setAuthError('Add client requires a signed-in staff (Practitioner) session.');
      return;
    }
    if (action === 'ready') {
      setReady(true);
      setAuthError(null);
      return;
    }

    const { clientId, clientSecret, notConfigured } = credentials ?? intakeCredentials();
    if (!clientId || !clientSecret) {
      setReady(false);
      setAuthError(notConfigured);
      return;
    }

    let cancelled = false;
    setReady(false);
    setAuthError(null);

    void medplum
      .startClientLogin(clientId, clientSecret)
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setAuthError(
            getUserFacingErrorMessage(e, 'Failed to authenticate public intake client'),
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, medplum, staffAddClient, credentials]);

  return { ready: enabled && ready, authError: enabled ? authError : null };
}

/**
 * Key prefix for the separate client's storage. Medplum's constructor installs a `storage`
 * listener that reloads the page or swaps tokens when `storage.makeKey('activeLogin')` changes
 * in another tab, and offers no way to remove it. With the default (empty) prefix that key is
 * the app's own `activeLogin`, so signing in or out in another tab would reload the guest page
 * mid-call. Under this prefix the key never exists in localStorage (the data lives in memory),
 * so the listener only ever reacts to a full `localStorage.clear()` — exactly as the app-wide
 * client already does.
 */
export const SEPARATE_INTAKE_STORAGE_PREFIX = 'unify.separateIntake.';

export function createSeparateIntakeStorage(): ClientStorage {
  return new ClientStorage(new MemoryStorage(), SEPARATE_INTAKE_STORAGE_PREFIX);
}

/** One per base URL and ClientApplication for the page's lifetime: each construction leaks an unremovable listener. */
const separateIntakeClients = new Map<string, MedplumClient>();

export function separateIntakeClientFor(baseUrl: string, clientId = ''): MedplumClient {
  const key = `${baseUrl}|${clientId}`;
  let client = separateIntakeClients.get(key);
  if (!client) {
    client = new MedplumClient({ baseUrl, storage: createSeparateIntakeStorage() });
    separateIntakeClients.set(key, client);
  }
  return client;
}

/**
 * A page-private public-intake client for a signed-in user whose own session cannot do the
 * job (e.g. a RelatedPerson opening `/join/:token`). In-memory storage under its own prefix,
 * so it never replaces, signs out, or reacts to the app-wide session the way
 * `startClientLogin` on `useMedplum()` would.
 */
export function useSeparateIntakeClient(
  enabled: boolean,
  /** Default: the SPA client (`VITE_MEDPLUM_CLIENT_ID`). */
  credentials?: ClientCredentials,
): {
  client: MedplumClient | undefined;
  authError: string | null;
} {
  const medplum = useMedplum();
  const [client, setClient] = useState<MedplumClient | undefined>();
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    setClient(undefined);
    setAuthError(null);
    if (!enabled) return undefined;
    const { clientId, clientSecret, notConfigured } = credentials ?? intakeCredentials();
    if (!clientId || !clientSecret) {
      setAuthError(notConfigured);
      return undefined;
    }
    let cancelled = false;
    const separate = separateIntakeClientFor(medplum.getBaseUrl(), clientId);
    separate
      .startClientLogin(clientId, clientSecret)
      .then(() => {
        if (!cancelled) setClient(separate);
      })
      .catch((e) => {
        if (!cancelled) {
          setAuthError(e instanceof Error ? e.message : 'Failed to authenticate public intake client');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, medplum, credentials]);

  return { client: enabled ? client : undefined, authError };
}

/** @deprecated Prefer `useIntakeClientAuth` via IntakeDataProvider. */
export function useEnsureIntakeClientCredentials(): void {
  useIntakeClientAuth();
}
